/**
 * Error boundary component to catch and display React errors gracefully.
 * Fresh, light design with calming aesthetics.
 */

import { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-mesh flex items-center justify-center p-4 relative overflow-hidden">
          {/* Floating blobs */}
          <div className="blob blob-coral w-72 h-72 -top-36 -left-36 opacity-30" />
          <div className="blob blob-lavender w-64 h-64 bottom-20 -right-32 opacity-30" />

          <div className="relative z-10 w-full max-w-md">
            <div className="card text-center animate-fade-in">
              {/* Error icon */}
              <div className="w-20 h-20 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-red-400 to-red-500 flex items-center justify-center shadow-soft">
                <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>

              {/* Error message */}
              <h1 className="font-display text-2xl font-bold text-sage-800 mb-2">
                Something went wrong
              </h1>
              <p className="text-sage-500 mb-6">
                An unexpected error occurred. Please try again.
              </p>

              {/* Error details (collapsible) */}
              {this.state.error && (
                <details className="mb-6 text-left">
                  <summary className="cursor-pointer text-sm text-sage-400 hover:text-sage-600 transition-colors">
                    View error details
                  </summary>
                  <pre className="mt-3 p-4 rounded-2xl bg-sage-100 text-sage-700 text-xs overflow-auto max-h-40">
                    {this.state.error.message}
                  </pre>
                </details>
              )}

              {/* Action buttons */}
              <div className="flex gap-3 justify-center">
                <button
                  onClick={this.handleReset}
                  className="btn btn-primary"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Try Again
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="btn btn-ghost"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reload Page
                </button>
              </div>
            </div>

            {/* Footer message */}
            <p className="mt-6 text-center text-sage-400 text-sm animate-fade-in" style={{ animationDelay: '200ms' }}>
              If this keeps happening, please contact support
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
